<template>
  <div class="settings gmap-settings">
    <div class="main-wrapper">
      <layout-header></layout-header>
      <layout-sidebarPromotion></layout-sidebarPromotion>

      <div class="page-wrapper">
        <div class="content container-fluid">
          <div class="page-header gmap-page-header">
            <div class="row align-items-center">
              <div class="col">
                <h3 class="page-title">Pengaturan Google Maps API Key</h3>
                <p class="gmap-page-subtitle">
                  Gunakan API Key Google Maps milik Anda sendiri untuk menjalankan Panen Kontak.
                </p>
              </div>
              <div class="col-auto">
                <router-link to="/farm-contact/list" class="btn btn-white">
                  <i class="fa fa-arrow-left"></i> Panen Kontak
                </router-link>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-lg-8">
              <div class="gmap-card">
                <div class="gmap-card-header">
                  <div>
                    <h4 class="gmap-card-title">API Key Google Maps</h4>
                    <p class="gmap-card-description">
                      Simpan sekali, lalu gunakan untuk semua pencarian kontak.
                    </p>
                  </div>
                  <span class="gmap-status" :class="statusClass">
                    <i class="fa" :class="statusIcon"></i>
                    {{ statusLabel }}
                  </span>
                </div>

                <form @submit.prevent="submit">
                  <div class="form-group gmap-form-group">
                    <label for="gmap-api-key">
                      API Key
                      <span class="text-danger">*</span>
                    </label>
                    <div class="gmap-input-wrap" :class="{ 'is-invalid': fieldError }">
                      <input
                        id="gmap-api-key"
                        v-model="dataParam.api_key"
                        :type="showKey ? 'text' : 'password'"
                        class="form-control gmap-input"
                        :disabled="isLoading || isPageLoading"
                        placeholder="Masukkan Google Maps API Key"
                        autocomplete="off"
                        autocapitalize="off"
                        spellcheck="false"
                        @input="clearFieldError"
                      />
                      <button
                        type="button"
                        class="gmap-input-action"
                        :aria-label="showKey ? 'Sembunyikan API Key' : 'Tampilkan API Key'"
                        :title="showKey ? 'Sembunyikan API Key' : 'Tampilkan API Key'"
                        @click="showKey = !showKey"
                      >
                        <i class="fa" :class="showKey ? 'fa-eye-slash' : 'fa-eye'"></i>
                      </button>
                    </div>

                    <p v-if="fieldError" class="gmap-field-error">{{ fieldError }}</p>
                    <p v-else-if="isConfigured" class="gmap-help">
                      API Key sudah tersimpan. Masukkan key baru hanya jika ingin menggantinya.
                    </p>
                    <p v-else class="gmap-help">
                      API Key tidak ditampilkan penuh secara default.
                    </p>
                  </div>

                  <div v-if="loadError" class="gmap-inline-alert is-warning">
                    <i class="fa fa-exclamation-circle"></i>
                    <span>{{ loadError }}</span>
                  </div>

                  <div v-if="formError" class="gmap-inline-alert is-error">
                    <i class="fa fa-exclamation-circle"></i>
                    <span>{{ formError }}</span>
                  </div>

                  <div v-if="successMessage" class="gmap-inline-alert is-success">
                    <i class="fa fa-check-circle"></i>
                    <span>{{ successMessage }}</span>
                  </div>

                  <div class="gmap-actions">
                    <button type="submit" class="btn btn-primary" :disabled="isLoading || isPageLoading">
                      <i v-if="isLoading" class="fa fa-spinner fa-spin"></i>
                      <i v-else class="fa fa-save"></i>
                      {{ isLoading ? "Menyimpan..." : "Simpan" }}
                    </button>
                    <a
                      class="gmap-guide-link"
                      href="https://developers.google.com/maps/documentation/javascript/get-api-key"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Cara mendapatkan API Key
                      <i class="fa fa-external-link"></i>
                    </a>
                  </div>
                </form>

                <div class="gmap-billing-note">
                  <i class="fa fa-info-circle"></i>
                  <span>API Key dikelola dan ditagihkan oleh Google, di luar paket Waajo.</span>
                </div>
              </div>
            </div>

            <div class="col-lg-4">
              <div class="gmap-requirements">
                <h5 class="gmap-requirements-title">Syarat penggunaan</h5>
                <ul class="gmap-requirements-list">
                  <li>
                    <i class="fa fa-check-circle"></i>
                    <span>Google Maps API Key milik sendiri.</span>
                  </li>
                  <li>
                    <i class="fa fa-check-circle"></i>
                    <span>Minimal satu lisensi WhatsApp berbayar yang aktif.</span>
                  </li>
                  <li>
                    <i class="fa fa-check-circle"></i>
                    <span>API Key cukup disetel sekali untuk semua pencarian.</span>
                  </li>
                </ul>
                <router-link to="/license/list" class="gmap-requirements-link">
                  Lihat lisensi saya
                  <i class="fa fa-arrow-right"></i>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LayoutHeader from "@/components/layouts/Header.vue";
import LayoutSidebarPromotion from "@/components/layouts/SidebarPromotion.vue";
import { getApiErrorMessage } from "@/utils/gmapContact";

export default {
  components: {
    LayoutHeader,
    LayoutSidebarPromotion
  },
  data() {
    return {
      isLoading: false,
      isPageLoading: true,
      isConfigured: false,
      showKey: false,
      fieldError: "",
      formError: "",
      loadError: "",
      successMessage: "",
      dataParam: {
        api_key: ""
      }
    };
  },
  computed: {
    statusLabel() {
      if (this.isPageLoading) {
        return "Memuat...";
      }
      return this.isConfigured ? "Tersimpan" : "Belum diatur";
    },
    statusClass() {
      if (this.isPageLoading) {
        return "is-neutral";
      }
      return this.isConfigured ? "is-success" : "is-warning";
    },
    statusIcon() {
      if (this.isPageLoading) {
        return "fa-spinner fa-spin";
      }
      return this.isConfigured ? "fa-check-circle" : "fa-exclamation-circle";
    }
  },
  created() {
    this.getGmapKey();
  },
  methods: {
    clearFieldError() {
      this.fieldError = "";
      this.formError = "";
      this.successMessage = "";
    },
    submit() {
      if (this.isLoading) {
        return;
      }

      const apiKey = String(this.dataParam.api_key || "").trim();
      this.fieldError = "";
      this.formError = "";
      this.successMessage = "";

      if (!apiKey) {
        this.fieldError = "API Key wajib diisi.";
        return;
      }

      this.isLoading = true;
      this.$store
        .dispatch("gmapKey/setupKey", { api_key: apiKey })
        .then(() => {
          this.dataParam.api_key = apiKey;
          this.isConfigured = true;
          this.successMessage =
            "API Key berhasil disimpan. Anda tidak perlu memasukkannya lagi saat mencari kontak.";
        })
        .catch(error => {
          this.formError = getApiErrorMessage(
            error,
            "API Key gagal disimpan. Silakan coba lagi."
          );
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    getGmapKey() {
      this.isPageLoading = true;
      this.loadError = "";
      this.$store
        .dispatch("gmapKey/getGmapKey")
        .then(response => {
          const apiKey = response && response.data ? response.data : "";
          this.dataParam.api_key = apiKey;
          this.isConfigured = !!String(apiKey).trim();
        })
        .catch(error => {
          this.loadError = getApiErrorMessage(
            error,
            "Status API Key belum dapat dimuat. Anda tetap dapat menyimpan API Key."
          );
        })
        .then(() => {
          this.isPageLoading = false;
        });
    }
  },
  name: "settingsGmapKey"
};
</script>

<style scoped>
.gmap-settings /deep/ .page-wrapper {
  background: #f8fafc;
}

.gmap-page-header {
  margin-bottom: 22px;
}

.gmap-page-subtitle {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  line-height: 1.5;
}

.gmap-card {
  padding: 26px;
  background: #ffffff;
  border: 1px solid #e8edf3;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
}

.gmap-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
}

.gmap-card-title {
  margin: 0;
  color: #172033;
  font-size: 18px;
  font-weight: 700;
}

.gmap-card-description {
  margin: 6px 0 0;
  color: #667085;
  font-size: 13px;
}

.gmap-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.gmap-status.is-success {
  color: #067647;
  background: #ecfdf3;
}

.gmap-status.is-warning {
  color: #b54708;
  background: #fffaeb;
}

.gmap-status.is-neutral {
  color: #667085;
  background: #f2f4f7;
}

.gmap-form-group label {
  color: #344054;
  font-weight: 600;
}

.gmap-input-wrap {
  position: relative;
}

.gmap-input {
  height: 46px;
  padding-right: 46px;
  border-color: #d0d5dd;
  border-radius: 9px;
}

.gmap-input:focus {
  border-color: #f99a43;
  box-shadow: 0 0 0 3px rgba(249, 154, 67, 0.15);
}

.gmap-input-wrap.is-invalid .gmap-input {
  border-color: #f04438;
}

.gmap-input-action {
  position: absolute;
  top: 50%;
  right: 6px;
  width: 34px;
  height: 34px;
  padding: 0;
  color: #667085;
  background: transparent;
  border: 0;
  border-radius: 7px;
  transform: translateY(-50%);
}

.gmap-input-action:hover {
  color: #b45309;
  background: #fff7ef;
}

.gmap-help {
  margin: 8px 0 0;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.5;
}

.gmap-field-error {
  margin: 8px 0 0;
  color: #d92d20;
  font-size: 12px;
  font-weight: 600;
}

.gmap-inline-alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 16px;
  padding: 11px 13px;
  border: 1px solid transparent;
  border-radius: 9px;
  font-size: 13px;
  line-height: 1.5;
}

.gmap-inline-alert.is-error {
  color: #b42318;
  background: #fef3f2;
  border-color: #fecdca;
}

.gmap-inline-alert.is-success {
  color: #067647;
  background: #ecfdf3;
  border-color: #abefc6;
}

.gmap-inline-alert.is-warning {
  color: #b54708;
  background: #fffaeb;
  border-color: #fedf89;
}

.gmap-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 22px;
}

.gmap-actions .btn {
  min-width: 122px;
  height: 42px;
  border-radius: 9px;
  font-weight: 700;
}

.gmap-guide-link {
  color: #b45309;
  font-size: 13px;
  font-weight: 600;
}

.gmap-guide-link:hover {
  color: #92400e;
  text-decoration: underline;
}

.gmap-billing-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 24px;
  padding-top: 18px;
  color: #667085;
  border-top: 1px solid #eef2f6;
  font-size: 13px;
  line-height: 1.5;
}

.gmap-billing-note i {
  margin-top: 2px;
  color: #f99a43;
}

.gmap-requirements {
  padding: 22px;
  background: #ffffff;
  border: 1px solid #e8edf3;
  border-radius: 14px;
}

.gmap-requirements-title {
  margin: 0 0 14px;
  color: #172033;
  font-size: 14px;
  font-weight: 700;
}

.gmap-requirements-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.gmap-requirements-list li {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 10px 0;
  color: #526071;
  border-top: 1px solid #f2f5f8;
  font-size: 13px;
  line-height: 1.5;
}

.gmap-requirements-list li:first-child {
  padding-top: 0;
  border-top: 0;
}

.gmap-requirements-list i {
  margin-top: 2px;
  color: #12b76a;
}

.gmap-requirements-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  color: #b45309;
  font-size: 13px;
  font-weight: 700;
}

.gmap-requirements-link:hover {
  color: #92400e;
  text-decoration: underline;
}

@media (max-width: 991.98px) {
  .gmap-requirements {
    margin-top: 18px;
  }
}

@media (max-width: 575.98px) {
  .gmap-card {
    padding: 20px;
  }

  .gmap-card-header {
    flex-direction: column;
  }

  .gmap-actions {
    align-items: stretch;
  }

  .gmap-actions .btn {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/settings/setupGmapKey.vue